import { components } from "react-select";

export const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <i className="material-icons-outlined fs-20">keyboard_arrow_down</i>
    </components.DropdownIndicator>
  );
};

export const ClearIndicator = (props) => {
  return (
    <components.ClearIndicator {...props}>
      <i className="material-icons-outlined mr-n3">close</i>
    </components.ClearIndicator>
  );
};

/** True when the trading platform root is in dark theme. */
export function isPlatformDark() {
  if (typeof document === "undefined") return false;
  return Boolean(document.querySelector(".trading-platform.dark"));
}

/** Shared react-select tweaks — fixes left-clipped placeholder on mobile (no custom components). */
export const selectFieldStyles = {
  valueContainer: (base) => ({
    ...base,
    padding: "2px 10px",
    overflow: "hidden",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    paddingTop: 0,
    paddingBottom: 0,
    gridTemplateColumns: "1fr",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    // react-select v5 ghost sizing row — clips placeholder on the left if left enabled
    "&:after": {
      ...(base["&:after"] || {}),
      content: "none",
      display: "none",
    },
  }),
  placeholder: (base) => ({
    ...base,
    marginLeft: 0,
    marginRight: 0,
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    gridArea: "1 / 1 / 2 / 3",
  }),
  singleValue: (base) => ({
    ...base,
    marginLeft: 0,
    marginRight: 0,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
};

const lightSelect = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "39px",
    fontSize: "0.875rem",
    color: "#666",
    border: state.isFocused ? "1px solid #885b24" : "1px solid #cad1d7",
    backgroundColor: state.isFocused ? "#e1ebff" : "white",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#885b24",
    },
  }),
  placeholder: (provided) => ({
    ...selectFieldStyles.placeholder(provided),
    color: "#888",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: "none",
  }),
  singleValue: (provided) => ({
    ...selectFieldStyles.singleValue(provided),
  }),
  input: (provided) => ({
    ...selectFieldStyles.input(provided),
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 1000,
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: "0.875rem",
    color: state.isSelected ? "#fff" : "#666",
    backgroundColor: state.isSelected
      ? "#885b24"
      : state.isFocused
      ? "#d9d9d9"
      : "#ffffff",
    ":active": {
      backgroundColor: "#885b24",
      color: "#fff",
    },
  }),
};

const darkSelect = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "39px",
    fontSize: "0.875rem",
    color: "#e8e4df",
    border: state.isFocused ? "1px solid #2f9e93" : "1px solid #3a3a3a",
    backgroundColor: "#1a1a1a",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#2f9e93",
    },
  }),
  placeholder: (provided) => ({
    ...selectFieldStyles.placeholder(provided),
    color: "#6b7280",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: "#9ca3af",
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    display: "none",
  }),
  singleValue: (provided) => ({
    ...selectFieldStyles.singleValue(provided),
    color: "#e8e4df",
  }),
  input: (provided) => ({
    ...selectFieldStyles.input(provided),
    color: "#e8e4df",
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 1000,
    backgroundColor: "#1a1a1a",
    border: "1px solid #3a3a3a",
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: "0.875rem",
    color: state.isSelected ? "#fff" : "#e8e4df",
    backgroundColor: state.isSelected
      ? "#1A7A70"
      : state.isFocused
      ? "#252525"
      : "#1a1a1a",
    ":active": {
      backgroundColor: "#1A7A70",
      color: "#fff",
    },
  }),
};

/**
 * Theme-aware react-select styles. Reads `.trading-platform.dark` at style time
 * so Segment / Client / etc. dropdowns match dark mode everywhere.
 */
export const stylesSelect = {
  ...selectFieldStyles,
  control: (provided, state) =>
    (isPlatformDark() ? darkSelect : lightSelect).control(provided, state),
  placeholder: (provided) =>
    (isPlatformDark() ? darkSelect : lightSelect).placeholder(provided),
  dropdownIndicator: (provided, state) =>
    (isPlatformDark() ? darkSelect : lightSelect).dropdownIndicator(provided, state),
  indicatorSeparator: (provided, state) =>
    (isPlatformDark() ? darkSelect : lightSelect).indicatorSeparator(provided, state),
  singleValue: (provided, state) =>
    (isPlatformDark() ? darkSelect : lightSelect).singleValue(provided, state),
  input: (provided) =>
    (isPlatformDark() ? darkSelect : lightSelect).input(provided),
  menu: (provided, state) =>
    (isPlatformDark() ? darkSelect : lightSelect).menu(provided, state),
  option: (provided, state) =>
    (isPlatformDark() ? darkSelect : lightSelect).option(provided, state),
};

/** Explicit dark styles (when caller already knows theme). */
export const stylesSelectDark = {
  ...selectFieldStyles,
  ...darkSelect,
};

export const stylesSelectAlternative = {
  ...selectFieldStyles,
  control: (provided, state) => {
    if (isPlatformDark()) return darkSelect.control(provided, state);
    return {
      ...provided,
      minHeight: "43px",
      fontSize: "0.875rem",
      color: "#adb5bd",
      border: "0",
      boxShadow: "0 1px 3px rgb(50 50 93 / 15%), 0 1px 0 rgb(0 0 0 / 2%)",
    };
  },
  placeholder: (provided) => {
    if (isPlatformDark()) return darkSelect.placeholder(provided);
    return {
      ...selectFieldStyles.placeholder(provided),
      color: "#888",
    };
  },
  dropdownIndicator: (provided, state) => {
    if (isPlatformDark()) return darkSelect.dropdownIndicator(provided, state);
    return { ...provided };
  },
  indicatorSeparator: (provided) => ({
    ...provided,
    display: "none",
  }),
  singleValue: (provided, state) => {
    if (isPlatformDark()) return darkSelect.singleValue(provided, state);
    return { ...selectFieldStyles.singleValue(provided) };
  },
  input: (provided) => {
    if (isPlatformDark()) return darkSelect.input(provided);
    return selectFieldStyles.input(provided);
  },
  menu: (provided, state) => {
    if (isPlatformDark()) return darkSelect.menu(provided, state);
    return { ...provided, zIndex: 1000 };
  },
  option: (provided, state) => {
    if (isPlatformDark()) return darkSelect.option(provided, state);
    return {
      ...provided,
      color: state.isFocused || state.isSelected ? "#ffffff" : "#32325d",
      backgroundColor:
        state.isFocused || state.isSelected ? "#885b24" : "#ffffff",
    };
  },
};
